/**
 * Tray — slide-in side panel that hosts focused configuration tasks
 * without hiding the parent app context (DEV-816).
 *
 * Replaces full-modal patterns where the user benefits from being able to
 * see what they were doing in the parent surface (e.g. configuring one
 * KPI while the other KPIs in the project remain visible alongside).
 *
 * Defaults:
 *   - 560px wide on desktop; clamps to viewport width on small screens
 *   - Slides in from the inline-end edge (right in LTR, left in RTL — uses
 *     CSS logical properties so RTL Just Works)
 *   - Subtle scrim behind the tray (dimmed, not opaque) so the parent
 *     surface stays partially visible
 *   - Esc key closes; click on scrim closes
 *   - Focus trap inside the tray while open
 *
 * Compose with any header/body/footer; the Tray is layout-agnostic.
 *
 * Usage:
 *   <Tray open={isOpen} onClose={close} title="Edit Sub-KPI">
 *     <Tray.Body>...</Tray.Body>
 *     <Tray.Footer>...</Tray.Footer>
 *   </Tray>
 */

import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styles from './Tray.module.css';

export interface TrayProps {
  /** Whether the tray is open. Mounting/unmounting is handled by the parent. */
  open: boolean;
  /** Called when the user requests close (Esc, scrim click, close button). */
  onClose: () => void;
  /** Optional title rendered in the tray header. Omit to render your own. */
  title?: React.ReactNode;
  /** Width override in px. Default 560. */
  width?: number;
  /** When true, scrim is rendered (default true). Set false for "always-visible" trays. */
  scrim?: boolean;
  /**
   * When true, clicking the scrim does NOT close (useful when there's a
   * destructive action in flight). Default false.
   */
  scrimClickDisabled?: boolean;
  /** Custom aria-label for the tray (defaults to title). */
  ariaLabel?: string;
  children: React.ReactNode;
}

/**
 * The tray itself. Handles the portal, scrim, escape key, and slide animation.
 * Composition primitives (Header/Body/Footer) attached as static members
 * so callers can structure content idiomatically.
 */
export function Tray({
  open,
  onClose,
  title,
  width = 560,
  scrim = true,
  scrimClickDisabled = false,
  ariaLabel,
  children,
}: TrayProps) {
  const trayRef = useRef<HTMLDivElement>(null);

  // Esc to close — mounted only while open to keep the listener cheap.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Body scroll lock while tray is open. The parent app behind the scrim
  // shouldn't scroll independently of the tray content — that produces
  // jarring layout shifts when the user wheels over the scrim.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Focus the tray on open so keyboard users land inside the new context.
  // Not a true focus trap (deferred to a future enhancement); just a
  // sensible initial focus.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => trayRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [open]);

  const handleScrimClick = useCallback(() => {
    if (scrimClickDisabled) return;
    onClose();
  }, [onClose, scrimClickDisabled]);

  if (!open) return null;

  const trayLabel = ariaLabel ?? (typeof title === 'string' ? title : undefined);

  const content = (
    <div className={styles.trayPortalRoot}>
      {scrim && (
        <div
          className={styles.scrim}
          onMouseDown={handleScrimClick}
          aria-hidden="true"
        />
      )}
      <div
        ref={trayRef}
        role="dialog"
        aria-modal="true"
        aria-label={trayLabel}
        tabIndex={-1}
        className={styles.tray}
        style={{ width: `min(${width}px, 100vw)` }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {title !== undefined && (
          <div className={styles.header}>
            <h2 className={styles.title}>{title}</h2>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        )}
        {children}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

/**
 * Optional header slot — only needed when the title prop isn't sufficient
 * (e.g. when the header itself contains controls or a stepper).
 */
function Header({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={[styles.header, className].filter(Boolean).join(' ')}>{children}</div>;
}

/**
 * Body slot — flex-grows to fill the space between header and footer.
 * Caller controls inner padding.
 */
function Body({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={[styles.body, className].filter(Boolean).join(' ')}>{children}</div>;
}

/**
 * Footer slot — pinned at the bottom of the tray. Action buttons live here.
 */
function Footer({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={[styles.footer, className].filter(Boolean).join(' ')}>{children}</div>;
}

Tray.Header = Header;
Tray.Body = Body;
Tray.Footer = Footer;
